import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import { TabsComponent } from '@enechain/ecloud-designsystem'

import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { todayCivilDate } from '~/models/civilDate'
import { OwnNoticesView } from '~/pages/SplitSupplyPlan/components/OwnNoticesView'
import { PartnerNoticesView } from '~/pages/SplitSupplyPlan/components/PartnerNoticesView'
import { SectionFilterBar } from '~/pages/SplitSupplyPlan/components/SectionFilterBar'
import { usePersistedSplitSupplyPlanFilters } from '~/pages/SplitSupplyPlan/hooks/usePersistedSplitSupplyPlanFilters'

/** 上段のタブ: 自社の通告値 (自社が出す側) / 他社の通告値 (他社が出した写し)。 */
const SIDES = ['own', 'partner'] as const
type Side = (typeof SIDES)[number]

/**
 * 分割供給計画の一覧。 上段のタブで「自社の通告値」(自社が通告値を出す契約: 作成・48 コマの編集・
 * XML ダウンロード) と「他社の通告値」(他社が通告値を出す契約: 受け取った値の登録・手直し) を分ける。
 * その下の断面 (翌日 / 月間)・対象日 (月間は対象月)・エリアの絞り込みは両方で共通で、
 * 上段のタブを切り替えても保たれる。 広域機関への提出は含まない。
 * `?side=partner` で他社の通告値のタブを開く (詳細からの戻り)。
 */
export const SplitSupplyPlanListPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const today = useMemo(() => todayCivilDate(), [])
  const [side, setSide] = useState<Side>(
    searchParams.get('side') === 'partner' ? 'partner' : 'own',
  )
  const { tab, setTab, target, setTarget, area, setArea } =
    usePersistedSplitSupplyPlanFilters()

  const breadcrumbItems = [
    { label: t('common:nav.plan') },
    { label: t('common:route.planSplitSupply'), bold: true },
  ]

  return (
    <PageLayout breadcrumbItems={breadcrumbItems}>
      <PageBody>
        <PageIntro description={t('page:splitSupplyPlan.description')} />
        <TabsComponent
          items={SIDES.map((key) => ({
            key,
            label: t(`page:splitSupplyPlan.side.${key}`),
          }))}
          onSelect={(key: Side) => {
            setSide(key)
          }}
          selected={side}
          tabSx={{ fontSize: tabTriggerFontSize.raw }}
        />
        <SectionFilterBar
          area={area}
          onAreaChange={setArea}
          onTabChange={setTab}
          onTargetChange={setTarget}
          tab={tab}
          target={target}
          today={today}
        />
        {side === 'own' ? (
          <OwnNoticesView
            area={area}
            onTargetChange={setTarget}
            tab={tab}
            target={target}
            today={today}
          />
        ) : (
          <PartnerNoticesView
            area={area}
            tab={tab}
            target={target}
            today={today}
          />
        )}
      </PageBody>
    </PageLayout>
  )
}
